import {
  findDuplicateLabels,
  matchPastedTariffs,
  splitSideBySideBlocks,
  tagColumn,
  type ExistingTariff,
} from './multiTariffPaste';
import { getGridColumnCount, parsePastedGrid } from './parsePastedGrid';
import {
  parseRoomTariffRows,
  ROOM_BLOCK_COLUMNS,
  ROOM_BLOCK_ROWS,
  type PastedRoomTariff,
  type RoomPasteTarget,
} from './parseRoomTariffBlock';
import type {
  ChildBandsCount,
  ExcelPasteIssue,
  ExcelPasteResult,
} from './types';

/**
 * Paste of every tariff of a category at once: the 28×2 blocks of the
 * "Catégorie" tab side by side, 28 lines × 2 columns per tariff. A pair of
 * columns left entirely empty is skipped, so the grid can always offer the
 * same number of slots whatever the number of tariffs.
 */
export function parseRoomCategoryBlock(
  text: string,
  childBandsCount: ChildBandsCount,
): ExcelPasteResult<PastedRoomTariff[]> {
  const rows = parsePastedGrid(text);
  if (rows.length !== ROOM_BLOCK_ROWS) {
    return {
      value: null,
      errors: [{ code: 'roomRowCount', params: { received: rows.length } }],
      warnings: [],
    };
  }

  const columnCount = getGridColumnCount(rows);
  if (columnCount % ROOM_BLOCK_COLUMNS !== 0) {
    return {
      value: null,
      errors: [{ code: 'categoryColumns', params: { received: columnCount } }],
      warnings: [],
    };
  }

  const errors: ExcelPasteIssue[] = [];
  const tariffs: PastedRoomTariff[] = [];
  splitSideBySideBlocks(rows, ROOM_BLOCK_COLUMNS).forEach(
    ({ inColumn, rows: tariffRows }) => {
      const parsed = parseRoomTariffRows(tariffRows, childBandsCount);
      errors.push(...tagColumn(parsed.errors, inColumn));
      if (parsed.value) tariffs.push(parsed.value);
    },
  );

  if (errors.length === 0 && tariffs.length === 0) {
    errors.push({ code: 'noCategoryTariff' });
  }

  errors.push(...findDuplicateLabels(tariffs.map(({ name }) => name)));

  if (errors.length > 0) return { value: null, errors, warnings: [] };
  return { value: tariffs, errors: [], warnings: [] };
}

/** Same as matchPastedTariffs, for room tariffs. */
export const matchPastedRoomTariffs = (
  pasted: PastedRoomTariff[],
  existing: ExistingTariff[],
): { targets: RoomPasteTarget[]; newTariffCount: number } =>
  matchPastedTariffs(pasted, existing);
